import React, { useEffect, useState } from "react";
import axios from "axios";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const API_BASE = process.env.REACT_APP_API_BASE;

const PALETTE = [
    "#3d5a1a", "#a05a2c", "#efa208", "#3b5998", "#7a9c5c",
    "#c82333", "#17a2b8", "#6f42c1", "#e83e8c", "#20c997",
];

function StatCard({ icon, label, value, color, sub }) {
    return (
        <div className="col-6 col-md-4 col-xl-3">
            <div
                className="card border-0 shadow-sm h-100"
                style={{ borderRadius: 16, background: "#fff" }}
            >
                <div className="card-body d-flex align-items-center">
                    <div
                        className="d-flex align-items-center justify-content-center me-3"
                        style={{
                            width: 52,
                            height: 52,
                            borderRadius: 14,
                            background: `${color}22`,
                            color: color,
                            fontSize: 26,
                            flexShrink: 0,
                        }}
                    >
                        <i className={`bi ${icon}`}></i>
                    </div>
                    <div className="text-truncate">
                        <div className="fw-bold" style={{ fontSize: "1.55rem", color: "#2d3a4a", lineHeight: 1.1 }}>
                            {value}
                        </div>
                        <div className="text-muted text-truncate" style={{ fontSize: "0.9rem" }}>{label}</div>
                        {sub && (
                            <div style={{ fontSize: "0.78rem", color: color, fontWeight: 600 }}>{sub}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ChartCard({ title, children }) {
    return (
        <div className="col-12 col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
                <div className="card-body">
                    <h6 className="fw-bold mb-3" style={{ color: "#3d5a1a" }}>{title}</h6>
                    <div style={{ position: "relative", height: 300 }}>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function AdminDashboard({ setView }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchStats = async () => {
            setLoading(true);
            setError("");
            try {
                const token = localStorage.getItem("token");
                const res = await axios.get(`${API_BASE}/api/admin/dashboard-stats`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setStats(res.data);
            } catch (err) {
                console.error("Error loading dashboard stats:", err);
                setError(err?.response?.data?.error || "Failed to load dashboard statistics");
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
                <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger text-center">{error}</div>
            </div>
        );
    }

    if (!stats) return null;

    const { devotees, users, sadhana, misc } = stats;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "bottom" } },
    };

    const roleChart = {
        labels: users.byRole.map((r) => r.label),
        datasets: [
            {
                data: users.byRole.map((r) => r.count),
                backgroundColor: users.byRole.map((_, i) => PALETTE[i % PALETTE.length]),
                borderWidth: 0,
            },
        ],
    };

    const userTypeChart = {
        labels: users.byType.map((r) => r.label),
        datasets: [
            {
                data: users.byType.map((r) => r.count),
                backgroundColor: users.byType.map((_, i) => PALETTE[(i + 2) % PALETTE.length]),
                borderWidth: 0,
            },
        ],
    };

    const genderChart = {
        labels: devotees.byGender.map((r) => r.label),
        datasets: [
            {
                label: "Devotees",
                data: devotees.byGender.map((r) => r.count),
                backgroundColor: devotees.byGender.map((_, i) => PALETTE[i % PALETTE.length]),
                borderRadius: 8,
            },
        ],
    };

    const trendLabels = sadhana.trend.map((t) => {
        const d = new Date(t.label);
        return isNaN(d.getTime()) ? t.label : `${d.getDate()}/${d.getMonth() + 1}`;
    });
    const trendChart = {
        labels: trendLabels,
        datasets: [
            {
                label: "Sadhana entries",
                data: sadhana.trend.map((t) => t.count),
                borderColor: "#3d5a1a",
                backgroundColor: "rgba(61,90,26,0.15)",
                fill: true,
                tension: 0.35,
                pointRadius: 3,
            },
        ],
    };

    return (
        <div className="container-fluid px-2 px-md-4 mt-3 mt-md-4" style={{ maxWidth: 1300 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <h4 className="fw-bold mb-0" style={{ color: "#3d5a1a" }}>
                    <i className="bi bi-speedometer2 me-2"></i>Admin Dashboard
                </h4>
                <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                    Overview of devotees, users &amp; sadhana activity
                </span>
            </div>

            {/* Stat cards */}
            <div className="row g-2 g-md-3">
                <StatCard icon="bi-people-fill" label="Total Devotees" value={devotees.total} color="#3d5a1a" />
                <StatCard icon="bi-person-badge-fill" label="Total Users" value={users.total} color="#3b5998" />
                <StatCard icon="bi-gem" label="Active Premium" value={users.activePremium} color="#6f42c1" />
                <StatCard icon="bi-hourglass-split" label="Active Trial" value={users.activeTrial} color="#efa208" />
                <StatCard icon="bi-x-octagon-fill" label="Expired Plans" value={users.expired} color="#c82333" />
                <StatCard icon="bi-journal-check" label="Sadhana Entries" value={sadhana.totalEntries} color="#a05a2c" sub={`${sadhana.today} today`} />
                <StatCard icon="bi-calendar-week" label="Entries (7 days)" value={sadhana.last7Days} color="#17a2b8" />
                <StatCard icon="bi-card-image" label="Sadhana Cards" value={misc.sadhanaCards} color="#7a9c5c" />
                <StatCard icon="bi-envelope-fill" label="Notifications" value={misc.notifications} color="#e83e8c" sub={`${misc.unreadNotifications} unread`} />
                <StatCard icon="bi-check2-square" label="To-Do Items" value={misc.todos} color="#20c997" />
            </div>

            {/* Charts */}
            <div className="row mt-2">
                <ChartCard title="Sadhana Entries — Last 14 Days">
                    {sadhana.trend.length > 0 ? (
                        <Line data={trendChart} options={chartOptions} />
                    ) : (
                        <p className="text-muted text-center mt-5">No recent entries</p>
                    )}
                </ChartCard>
                <ChartCard title="Devotees by Gender">
                    {devotees.byGender.length > 0 ? (
                        <Bar data={genderChart} options={{ ...chartOptions, plugins: { legend: { display: false } } }} />
                    ) : (
                        <p className="text-muted text-center mt-5">No data</p>
                    )}
                </ChartCard>
                <ChartCard title="Users by Role">
                    {users.byRole.length > 0 ? (
                        <Doughnut data={roleChart} options={chartOptions} />
                    ) : (
                        <p className="text-muted text-center mt-5">No data</p>
                    )}
                </ChartCard>
                <ChartCard title="Users by Plan Type">
                    {users.byType.length > 0 ? (
                        <Doughnut data={userTypeChart} options={chartOptions} />
                    ) : (
                        <p className="text-muted text-center mt-5">No data</p>
                    )}
                </ChartCard>
            </div>

            {/* Quick actions */}
            {setView && (
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
                    <div className="card-body">
                        <h6 className="fw-bold mb-3" style={{ color: "#3d5a1a" }}>Quick Actions</h6>
                        <div className="d-flex flex-wrap gap-2">
                            <button className="btn btn-outline-success" onClick={() => setView("view")}>
                                <i className="bi bi-people me-1"></i> View Devotees
                            </button>
                            <button className="btn btn-outline-primary" onClick={() => setView("add")}>
                                <i className="bi bi-person-plus me-1"></i> Add / Update
                            </button>
                            <button className="btn btn-outline-secondary" onClick={() => setView("register")}>
                                <i className="bi bi-person-badge me-1"></i> Assign Role
                            </button>
                            <button className="btn btn-outline-warning" onClick={() => setView("adminUploadedReports")}>
                                <i className="bi bi-file-earmark-bar-graph me-1"></i> Devotee Reports
                            </button>
                            <button className="btn btn-outline-info" onClick={() => setView("sadhanaReports")}>
                                <i className="bi bi-bar-chart me-1"></i> Sadhana Reports
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
